import { Link } from 'react-router-dom';
import { useCompareProducts, type CompareProduct } from '../context/CompareContext';

function formatProductPrice(price: number, currency = 'USD') {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(price);
}

function getComparisonRows(items: CompareProduct[]) {
  const specs = new Set<string>();

  items.forEach((item) => {
    Object.keys(item.specifications ?? {}).forEach((key) => specs.add(key));
  });

  return [
    { label: 'Price', value: (item: CompareProduct) => formatProductPrice(item.price, item.currency) },
    { label: 'Category', value: (item: CompareProduct) => item.category },
    { label: 'Description', value: (item: CompareProduct) => item.description },
    ...Array.from(specs).map((key) => ({
      label: key,
      value: (item: CompareProduct) => item.specifications?.[key] ?? '—',
    })),
  ];
}

export function Compare() {
  const { items, removeItem, clearItems } = useCompareProducts();

  if (items.length === 0) {
    return (
      <section className="section bg-gray-50">
        <div className="container-main">
          <div className="max-w-2xl mx-auto text-center bg-white rounded-2xl border border-gray-200 p-10 shadow-sm">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Your quote list is empty</h1>
            <p className="text-gray-600 mb-8">
              Add products to your list, then send one request to our team for pricing, availability, and delivery details.
            </p>
            <Link to="/products" className="btn-primary">
              Browse products
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const comparisonRows = getComparisonRows(items);
  const bestValue = [...items].sort((a, b) => a.price - b.price)[0];

  return (
    <section className="section bg-gray-50">
      <div className="container-main">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-sm uppercase tracking-wide text-green-700 font-semibold">Customer workspace</p>
            <h1 className="text-3xl font-bold text-gray-900">Your quote list</h1>
            <p className="mt-2 text-gray-600">Choose the products you want us to price and prepare for you.</p>
          </div>
          <button type="button" onClick={clearItems} className="btn-outline">
            Clear all
          </button>
        </div>

        {bestValue && (
          <div className="mb-6 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            Starting point: <span className="font-semibold">{bestValue.name}</span> at {formatProductPrice(bestValue.price, bestValue.currency)}. Final pricing depends on configuration and availability.
          </div>
        )}

        <div className="overflow-x-auto bg-white rounded-2xl border border-gray-200 shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Product detail</th>
                {items.map((item) => (
                  <th key={item.id} className="px-4 py-4 text-left align-top min-w-[220px]">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <Link to={`/products/${item.slug}`} className="block text-lg font-semibold text-gray-900 hover:text-green-700">
                          {item.name}
                        </Link>
                        <p className="text-sm text-gray-500">{item.category}</p>
                        {item.featured && <span className="mt-2 inline-block badge badge-green">Featured</span>}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="text-xs text-gray-500 hover:text-red-600"
                        aria-label={`Remove ${item.name} from compare`}
                      >
                        Remove
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-4 text-sm font-semibold text-gray-700">Image</td>
                {items.map((item) => (
                  <td key={`${item.id}-image`} className="px-4 py-4">
                    <div className="w-full aspect-video rounded-lg bg-gray-100 overflow-hidden">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-gray-400">
                          <span className="text-xs">Image unavailable</span>
                        </div>
                      )}
                    </div>
                  </td>
                ))}
              </tr>

              {comparisonRows.map((row) => (
                <tr key={row.label}>
                  <td className="px-4 py-4 text-sm font-semibold text-gray-700">{row.label}</td>
                  {items.map((item) => (
                    <td key={`${item.id}-${row.label}`} className="px-4 py-4 text-sm text-gray-600">
                      {row.value(item)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
